import { useEffect, useRef, useState, useCallback } from 'react';
import { Wifi } from 'lucide-react';

const QUICK_ACTIONS = [
  { label: 'Hand Hygiene', prompt: 'What are the key steps for proper hand hygiene?' },
  { label: 'Safe Transfers', prompt: 'How do I safely transfer a client from bed to wheelchair?' },
  { label: 'Pressure Injuries', prompt: 'What are the signs and stages of pressure injuries?' },
  { label: 'Choking Response', prompt: 'What should I do if a client shows signs of choking?' },
  { label: 'Vital Signs', prompt: 'What are normal vital sign ranges for adults?' },
  { label: 'Dementia Care', prompt: 'How do I provide person-centred dementia care?' },
];

const KNOWLEDGE_TABS = [
  { label: 'Knowledge Base', tab: 'kb' },
  { label: 'First Aid', tab: 'firstaid' },
  { label: 'Medications', tab: 'meds' },
  { label: 'Safety', tab: 'safety' },
  { label: 'Vitals', tab: 'vitals' },
  { label: 'Dementia', tab: 'dementia' },
];

export function HCAResourcePage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [activeKnowledge, setActiveKnowledge] = useState<string | null>(null);

  const injectStyles = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      const style = doc.createElement('style');
      const parentRoot = iframeRef.current?.closest('.admin-root') || document.querySelector('.admin-root');
      const cs = parentRoot ? getComputedStyle(parentRoot) : null;
      const surfaceColor = cs?.getPropertyValue('--admin-surface')?.trim() || 'rgb(42 35 32)';
      const surface2Color = cs?.getPropertyValue('--admin-surface-2')?.trim() || 'rgb(51 45 40)';
      const textPrimary = cs?.getPropertyValue('--admin-text-primary')?.trim() || 'rgb(245 235 224)';
      const textSecondary = cs?.getPropertyValue('--admin-text-secondary')?.trim() || 'rgb(187 168 144)';
      const textMuted = cs?.getPropertyValue('--admin-text-muted')?.trim() || 'rgb(138 123 106)';
      const borderColor = cs?.getPropertyValue('--admin-border')?.trim() || 'rgb(196 149 107 / .3)';
      style.textContent = `
        .sidebar { display: none !important; }
        .topbar { display: none !important; }
        .sidebar-disclaimer { display: none !important; }
        .quick-buttons { display: none !important; }
        body, html { background: ${surfaceColor} !important; }
        .main { margin-left: 0 !important; background: ${surfaceColor} !important; padding: 0 !important; }
        .chat-area { background: ${surfaceColor} !important; color: ${textPrimary} !important; }
        .tab-content { background: ${surfaceColor} !important; color: ${textPrimary} !important; }
        .tab-content.active { background: ${surfaceColor} !important; }
        .chat-input-area { background: ${surfaceColor} !important; padding: 12px 0 !important; }
        .input-row { background: ${surface2Color} !important; border: 1px solid ${borderColor} !important; border-radius: 16px !important; }
        .input-row input, .input-row textarea { background: transparent !important; color: ${textPrimary} !important; }
        .input-row button { color: ${textPrimary} !important; }
        .message { color: ${textPrimary} !important; }
        .bot-message { color: ${textSecondary} !important; }
        * { scrollbar-color: ${surface2Color} transparent; }
      `;
      doc.head.appendChild(style);
      const input = doc.querySelector('.input-row input, .input-row textarea') as HTMLInputElement;
      if (input) input.placeholder = 'Ask a care question (e.g., wound care, vitals, safety...)';
      setReady(true);
    } catch (e) {
      console.error('Failed to inject styles into iframe:', e);
    }
  }, []);

  const sendQuick = (prompt: string) => {
    try {
      const win = iframeRef.current?.contentWindow as any;
      if (win?.sendQuick) {
        win.showTab?.('chat');
        setActiveKnowledge(null);
        win.sendQuick(prompt);
      }
    } catch (e) { /* cross-origin fallback */ }
  };

  const switchTab = (tab: string) => {
    try {
      const win = iframeRef.current?.contentWindow as any;
      if (win?.showTab) {
        win.showTab(tab);
        setActiveKnowledge(tab);
      }
    } catch (e) { /* cross-origin fallback */ }
  };

  const goToChat = () => {
    try {
      const win = iframeRef.current?.contentWindow as any;
      if (win?.showTab) {
        win.showTab('chat');
        setActiveKnowledge(null);
      }
    } catch (e) { /* cross-origin fallback */ }
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => {
      setTimeout(injectStyles, 100);
    };
    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [injectStyles]);

  return (
    <main className="flex-1 p-6 space-y-4 flex flex-col h-[calc(100vh-2rem)]" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[rgb(var(--admin-text-primary))]">HCA Assistant</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">Quick access to care knowledge and guidance</p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-[rgb(var(--admin-text-muted))]">
          <Wifi className="w-3 h-3" />
          Connected
        </span>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-3">
        {QUICK_ACTIONS.map((action) => (
          <button
            key={action.label}
            onClick={() => sendQuick(action.prompt)}
            className="rounded-2xl px-4 py-3 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] text-sm text-[rgb(var(--admin-text-secondary))] text-left hover:bg-[rgb(var(--admin-surface-2))] transition-colors duration-200"
          >
            {action.label}
          </button>
        ))}
      </div>

      {/* Knowledge Access Row */}
      <div className="flex items-center gap-3">
        <button
          onClick={goToChat}
          className={`text-xs px-3 py-1.5 rounded-xl transition-colors duration-200 ${
            !activeKnowledge
              ? 'bg-[rgb(var(--admin-accent)_/_0.15)] text-[rgb(var(--admin-accent))]'
              : 'text-[rgb(var(--admin-text-muted))] hover:text-[rgb(var(--admin-text-secondary))]'
          }`}
        >
          Chat
        </button>
        {KNOWLEDGE_TABS.map((kt) => (
          <button
            key={kt.tab}
            onClick={() => switchTab(kt.tab)}
            className={`text-xs px-3 py-1.5 rounded-xl transition-colors duration-200 ${
              activeKnowledge === kt.tab
                ? 'bg-[rgb(var(--admin-accent)_/_0.15)] text-[rgb(var(--admin-accent))]'
                : 'text-[rgb(var(--admin-text-muted))] hover:text-[rgb(var(--admin-text-secondary))]'
            }`}
          >
            {kt.label}
          </button>
        ))}
      </div>

      {/* Assistant Container */}
      <div className="flex-1 rounded-3xl bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm overflow-hidden min-h-0">
        <iframe
          ref={iframeRef}
          src="/hca-assistant.html"
          className="w-full h-full border-0"
          title="HCA Assistant"
        />
      </div>
    </main>
  );
}